(() => {
  const GRID = '.product_grid_pagination, .collection__grid';
  const PAGINATION = '.pagination, .collection__pagination';
  const STATE_KEY = 'bssProductGrid';
  const controllers = new Map();

  let suspended = false;
  let restoring = false;
  let generation = 0;
  let activeUrl = '';
  let entryId = '';
  let scrollTimer;
  let restorePromise = Promise.resolve();

  const currentUrl = () => location.pathname + location.search;

  const mode = () =>
    document.body.className.match(/bss-grid-mode--([a-z_]+)/)?.[1] ||
    'pages';

  const savedState = () => {
    const state = history.state?.[STATE_KEY];

    return state?.version === 1 && state.url === currentUrl()
      ? state
      : null;
  };

  function savePosition() {
    if (
      suspended ||
      restoring ||
      mode() === 'pages' ||
      !controllers.size
    ) {
      return;
    }

    const state = savedState();

    if (
      !state ||
      state.id !== entryId ||
      activeUrl !== currentUrl()
    ) {
      return;
    }

    if (
      [...controllers.values()].some(c => !c.grid.isConnected)
    ) {
      return;
    }

    const grids = {};

    controllers.forEach(c => {
      grids[c.key] = { pages: [...c.pages] };
    });

    history.replaceState(
      {
        ...history.state,
        [STATE_KEY]: {
          ...state,
          grids,
          scrollY: window.scrollY
        }
      },
      ''
    );
  }

  function updateControls(c) {
    const hasMore = Boolean(c.next());

    c.pagination.style.display =
      mode() === 'infinite' ? 'none' : '';

    if (c.button) {
      c.button.style.display =
        mode() === 'load_more' && hasMore ? '' : 'none';

      c.button.disabled =
        c.loading || c.restoring || !hasMore;

      c.button.classList.toggle(
        'is-loading',
        c.loading || c.restoring
      );
    }
  }

  function refreshProducts() {
    if (
      typeof window.theme?.reinitProductGridItem === 'function'
    ) {
      window.theme.reinitProductGridItem();
    }
  }

  async function appendPage(c, pageUrl) {
    if (c.loading) return false;

    const url = new URL(pageUrl, location.href);

    if (url.origin !== location.origin) {
      throw new Error('Invalid pagination origin.');
    }

    c.loading = true;
    c.request = new AbortController();

    updateControls(c);

    try {
      const response = await fetch(url.href, {
        credentials: 'same-origin',
        signal: c.request.signal
      });

      if (!response.ok) {
        throw new Error(
          'Pagination request failed: ' + response.status
        );
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(
        html,
        'text/html'
      );

      const newGrid = c.grid.id
        ? doc.getElementById(c.grid.id)
        : doc.querySelectorAll(GRID)[c.index];

      const newRoot = newGrid && (
        newGrid.closest('.collection-content') ||
        newGrid.closest('[data-section-id]') ||
        newGrid.closest('section') ||
        newGrid.parentElement
      );

      const newPagination =
        newRoot?.querySelector(PAGINATION);

      if (!newGrid || !newPagination) {
        throw new Error(
          'The requested page has no matching grid or pagination.'
        );
      }

      if (
        suspended ||
        !c.grid.isConnected ||
        controllers.get(c.grid) !== c
      ) {
        return false;
      }

      const items = newGrid.querySelectorAll('.grid__item');

      if (!items.length) {
        throw new Error(
          'The requested page has no product cards.'
        );
      }

      // Append product cards only, excluding the collection JSON-LD.
      const fragment = document.createDocumentFragment();

      items.forEach(item => fragment.appendChild(item));

      c.grid.appendChild(fragment);
      c.pagination.innerHTML = newPagination.innerHTML;
      c.pages.push(url.href);

      return true;
    } finally {
      c.loading = false;
      c.request = null;

      updateControls(c);
    }
  }

  async function loadNext(c) {
    const next = c.next();

    if (!next || c.loading || c.restoring) return;

    c.observer?.disconnect();

    try {
      if (await appendPage(c, next.href)) {
        savePosition();
        refreshProducts();
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('Load more error:', error);
      }
    } finally {
      if (
        c.grid.isConnected &&
        controllers.get(c.grid) === c &&
        c.next()
      ) {
        c.observer?.observe(c.trigger);
      }
    }
  }

  function destroy(c) {
    c.request?.abort();
    c.observer?.disconnect();
    c.trigger?.remove();

    if (c.button) c.button.onclick = null;
  }

  function init() {
    if (suspended) return restorePromise;

    controllers.forEach((c, grid) => {
      if (!grid.isConnected) {
        destroy(c);
        controllers.delete(grid);
      }
    });

    const grids = [...document.querySelectorAll(GRID)];
    const seen = new Set();
    const candidates = [];

    grids.forEach((grid, index) => {
      const root =
        grid.closest('.collection-content') ||
        grid.closest('[data-section-id]') ||
        grid.closest('section') ||
        grid.parentElement;

      const pagination = root?.querySelector(PAGINATION);

      if (!pagination || seen.has(pagination)) return;

      seen.add(pagination);

      if (!controllers.has(grid)) {
        candidates.push({
          grid,
          root,
          pagination,
          index
        });
      }
    });

    // Already initialized: preserve the current products and handlers.
    if (!candidates.length) return restorePromise;

    if (window._infObs) {
      window._infObs.disconnect();
      window._infObs = null;
    }

    const saved = savedState();
    const token = ++generation;

    activeUrl = currentUrl();

    entryId =
      saved?.id ||
      Date.now() + '-' + Math.random().toString(36).slice(2);

    restoring = Boolean(saved) && mode() !== 'pages';

    candidates.forEach(({ grid, root, pagination, index }) => {
      const c = {
        grid,
        pagination,
        index,

        key:
          (root.id ||
            root.getAttribute('data-section-id') ||
            'grid') +
          ':' +
          (grid.id || index),

        button: root.querySelector('[data-bss-load-more]'),
        pages: [],
        loading: false,
        restoring,

        next: () => pagination.querySelector(
          'a.pagination__next, a[rel="next"], ' +
          '.meet_infinite_next a, .next a'
        )
      };

      controllers.set(grid, c);

      if (mode() === 'load_more' && c.button) {
        c.button.onclick = () => loadNext(c);
      }

      if (mode() === 'infinite') {
        c.trigger = document.createElement('div');
        c.trigger.style.height = '1px';

        grid.after(c.trigger);

        c.observer = new IntersectionObserver(
          entries => {
            if (
              entries.some(entry => entry.isIntersecting)
            ) {
              loadNext(c);
            }
          },
          { rootMargin: '300px' }
        );
      }

      updateControls(c);
    });

    if (mode() === 'pages') {
      return Promise.resolve();
    }

    if (!saved) {
      history.replaceState(
        {
          ...history.state,
          [STATE_KEY]: {
            version: 1,
            id: entryId,
            url: activeUrl,
            grids: {},
            scrollY: window.scrollY
          }
        },
        ''
      );
    }

    restorePromise = (async () => {
      try {
        for (const c of controllers.values()) {
          const pages =
            saved?.grids?.[c.key]?.pages || [];

          for (const url of pages) {
            if (token !== generation) return;

            if (!await appendPage(c, url)) break;
          }
        }

        if (token !== generation) return;

        if (saved) {
          refreshProducts();

          // Let the restored cards enter the layout before scrolling.
          await new Promise(resolve => {
            requestAnimationFrame(() => {
              requestAnimationFrame(resolve);
            });
          });

          if (token !== generation) return;

          window.scrollTo({
            top: saved.scrollY,
            behavior: 'instant'
          });
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn(
            'Pagination restoration error:',
            error
          );
        }
      } finally {
        if (token === generation) {
          restoring = false;

          controllers.forEach(c => {
            c.restoring = false;

            updateControls(c);

            if (c.next()) {
              c.observer?.observe(c.trigger);
            }
          });

          savePosition();
        }
      }
    })();

    return restorePromise;
  }

  window.BSS_ProductGridPagination = init;

  // Called before the theme replaces the collection through AJAX.
  init.beforeRender = function (updateURLHash) {
    clearTimeout(scrollTimer);

    // On Back, history already points at the destination entry.
    // Do not overwrite it with the collection being left behind.
    if (updateURLHash) savePosition();

    suspended = true;
    generation++;

    controllers.forEach(destroy);
    controllers.clear();

    restoring = false;
  };

  // Called after the theme finishes replacing the collection.
  init.afterRender = function () {
    suspended = false;
    return init();
  };

  init.hasSavedState = () => Boolean(savedState());

  // Avoid replacing a view the browser has already preserved.
  init.hasCurrentView = () => {
    const saved = savedState();

    return (
      !suspended &&
      Boolean(saved) &&
      saved.id === entryId &&
      activeUrl === currentUrl() &&
      controllers.size > 0 &&
      [...controllers.values()].every(
        c => c.grid.isConnected
      )
    );
  };

  window.addEventListener(
    'scroll',
    () => {
      clearTimeout(scrollTimer);

      if (!suspended && !restoring) {
        scrollTimer = setTimeout(savePosition, 150);
      }
    },
    { passive: true }
  );

  window.addEventListener('pagehide', () => {
    savePosition();

    controllers.forEach(c => c.request?.abort());
  });

  window.addEventListener('pageshow', event => {
    if (event.persisted && init.hasCurrentView()) {
      const saved = savedState();

      window.scrollTo({
        top: saved.scrollY,
        behavior: 'instant'
      });
    }
  });

  document.addEventListener('shopify:section:load', init);

  document.addEventListener(
    'ajaxProductGrid:updated',
    () => setTimeout(init, 200)
  );

  document.addEventListener('collection:reloaded', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();